import React, { useState, FC } from "react";

import { useApiPolling, useRightSidebarState } from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";

import { useSearchManagersInsightPosts } from "./hooks/useSearchManagersInsightPosts";
import {
  InsightPostDetailWithEditForm,
  InsightPostSearchForm,
  InsightPostCommentsWithInsightPost,
} from "../../../../components";

import { InsightPostType } from "~/domains";

import { useApisInsightPostsNew as useApisManagersInsightPostsNew } from "~/features/insight-posts/hooks/managers";
import { useApisInsightPostsInsightPostCommentsIndex } from "~/features/insight-posts/hooks/members";
import {
  FilterIconWithSortMenu,
  RightSidebarToggleIconWithLabel,
  InfiniteScroll,
} from "~/ui";

export const ManagersFeedbackInsightPostsIndex: FC = () => {
  const breadCrumbItems = [
    { label: "メモログ確認", href: "/managers/feedback/insight_posts" },
  ];

  const [selectGetCommentInsightPostId, setSelectGetCommentInsightPostId] =
    useState<string>("");

  const {
    isSearchOpen,
    isReplyOpen,
    searchOpenToggle,
    setReplyOpen,
    setClose,
    isOpen,
  } = useRightSidebarState("isClose");

  const { data: fetchNewData } = useApisManagersInsightPostsNew();
  const { data: insightPostCommentsIndexData, refetch: commentsRefetch } =
    useApisInsightPostsInsightPostCommentsIndex({
      insightPostId: selectGetCommentInsightPostId,
      config: {
        enabled: Boolean(selectGetCommentInsightPostId),
      },
    });

  const {
    insightPosts,
    hasNextPage,
    isLoading,
    keyword,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    selectableDropdownHasCommentIds,
    selectedDropdownHasCommentId,
    selectableDropdownSharedIds,
    selectedDropdownSharedId,
    startDate,
    endDate,
    sort,
    isBelongingEmployee,
    isBelongingSection,
    selectableJobCategories,
    selectedJobCategories,
    optionSelectableJobCategoryPhases,
    optionSelectedJobCategoryPhases,
    selectableDropdownEmployees,
    selectedDropdownEmployees,
    selectableDropdownDivisions,
    selectedDropdownDivisions,
    selectableDropdownSections,
    selectedDropdownSections,
    selectableDropdownArchivedEmployees,
    selectedDropdownArchivedEmployees,
    onDivisionChange,
    onSectionChange,
    onEmployeesChange,
    onArchivedEmployeesChange,
    updateIndexInsightPost,
    deleteIndexInsightPost,
    onChangeDateRange,
    onChangeJobCategoryPhases,
    setSort,
    fetchNextPage,
    onChangeKeyword,
    onChangeIsBelongingEmployee,
    onChangeIsBelongingSection,
    onSearchSubmit,
    onChangeJobCategories,
    onChangeDropdownHasBookmarkId,
    onChangeDropdownHasCommentId,
    onChangeDropdownSharedId,
    onConditionReset,
    refetch,
  } = useSearchManagersInsightPosts();

  // NOTE: Refetchしない場合のメモリの使用率をみるため一時的にコメントアウト
  useApiPolling({
    enabled: true,
    interval: 30000,
    pollingFunction: refetch,
    timeoutDuration: null,
  });

  useApiPolling({
    enabled: isReplyOpen && Boolean(selectGetCommentInsightPostId),
    interval: 1000,
    pollingFunction: commentsRefetch,
    timeoutDuration: null,
  });

  const openComment = (insightPostId: string) => {
    setSelectGetCommentInsightPostId(insightPostId);
    setReplyOpen();
  };

  return (
    <>
      <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
        <EmployeeLayoutMainContent navigations={[]} withRightSidebar>
          <FilterIconWithSortMenu
            likeSort={() => setSort("likeCountDesc")}
            bookmarkSort={() => setSort("bookmarkCountDesc")}
            createdAtAscSort={() => setSort("createdAtAsc")}
            createdAtDescSort={() => setSort("createdAtDesc")}
            currentSortType={sort}
            className="mt-6"
          />
          <InfiniteScroll
            itemsLength={insightPosts.length}
            nextFetchFunction={fetchNextPage}
            hasMore={hasNextPage}
            isLoading={isLoading}
            className="border-t-2 border-secondary-200"
            noContentMessage="メモログがありません"
          >
            {insightPosts.map((post, index) => (
              <InsightPostDetailWithEditForm
                key={post.id}
                insightPost={post}
                mentions={fetchNewData?.mentionableEmployees || []}
                selectableJobCategoryPhases={
                  fetchNewData?.selectableJobCategoryPhases || []
                }
                deleteItem={() => {
                  deleteIndexInsightPost(index);
                  if (selectGetCommentInsightPostId === post.id) {
                    setSelectGetCommentInsightPostId("");
                    setClose();
                  }
                }}
                updateItem={(newItem: InsightPostType) =>
                  updateIndexInsightPost(newItem, index)
                }
                openComment={openComment}
              />
            ))}
          </InfiniteScroll>
        </EmployeeLayoutMainContent>
        <EmployeeLayoutRightSidebar>
          <RightSidebarToggleIconWithLabel
            iconOnClick={searchOpenToggle}
            handleOnClose={setClose}
            label={isSearchOpen ? "検索条件" : "リプライ"}
            isOpen={isOpen}
          >
            {isSearchOpen && (
              <InsightPostSearchForm
                onSubmit={onSearchSubmit}
                onConditionReset={onConditionReset}
                date={{
                  startDate: startDate,
                  endDate: endDate,
                  onChange: onChangeDateRange,
                }}
                jobCategoryAndJobCategoryPhase={{
                  selectableJobCategories: selectableJobCategories,
                  selectedJobCategories: selectedJobCategories,
                  selectableJobCategoryPhases:
                    optionSelectableJobCategoryPhases,
                  selectedJobCategoryPhases: optionSelectedJobCategoryPhases,
                  onJobCategoryChange: onChangeJobCategories,
                  onJobCategoryPhaseChange: onChangeJobCategoryPhases,
                }}
                isBelongingEmployee={{
                  isChecked: isBelongingEmployee,
                  onChange: onChangeIsBelongingEmployee,
                }}
                isBelongingSection={{
                  isChecked: isBelongingSection,
                  onChange: onChangeIsBelongingSection,
                }}
                hasComment={{
                  options: selectableDropdownHasCommentIds,
                  value: selectedDropdownHasCommentId,
                  onChange: onChangeDropdownHasCommentId,
                }}
                hasBookmark={{
                  options: selectableDropdownHasBookmarkIds,
                  value: selectedDropdownHasBookmarkId,
                  onChange: onChangeDropdownHasBookmarkId,
                }}
                shared={{
                  options: selectableDropdownSharedIds,
                  value: selectedDropdownSharedId,
                  onChange: onChangeDropdownSharedId,
                }}
                divisionAndSections={{
                  selectableDivisions: selectableDropdownDivisions,
                  selectedDivisions: selectedDropdownDivisions,
                  selectableSections: selectableDropdownSections,
                  selectedSection: selectedDropdownSections,
                  selectableEmployees: selectableDropdownEmployees,
                  selectedEmployees: selectedDropdownEmployees,
                  selectableDropdownArchivedEmployees,
                  selectedDropdownArchivedEmployees,
                  onDivisionChange,
                  onSectionChange,
                  onEmployeesChange,
                  onArchivedEmployeesChange,
                }}
                searchKeyword={{
                  value: keyword,
                  onChange: onChangeKeyword,
                }}
              />
            )}
            {isReplyOpen && insightPostCommentsIndexData && (
              <InsightPostCommentsWithInsightPost
                className="mt-6"
                insightPost={insightPostCommentsIndexData.insightPost}
                mentions={fetchNewData?.mentionableEmployees || []}
                insightPostComments={
                  insightPostCommentsIndexData.insightPostComments || []
                }
                refetch={commentsRefetch}
              />
            )}
          </RightSidebarToggleIconWithLabel>
        </EmployeeLayoutRightSidebar>
      </EmployeeLayout>
    </>
  );
};
